// src/utils/PackageService.ts
/* eslint-disable @typescript-eslint/no-explicit-any */
import type {
  Package,
  PaymentResponse,
  PaymentInitiationRequest,
  PaymentInitiationResponse,
  SubscriptionResponse,
  PaymentMethod,
  BillingPeriod,
} from "../types/package";

const API_BASE = import.meta.env.VITE_API_BASE;

export class PackageService {
  // ==================== PUBLIC METHODS (for users) ====================

  // For user packages page (public endpoint) - only active, non-deleted packages
  async getPackages(
    type?: "job_seeker" | "whitelist_request" | "all",
  ): Promise<Package[]> {
    const url =
      type && type !== "all"
        ? `${API_BASE}/subscriptions/packages?type=${type}`
        : `${API_BASE}/subscriptions/packages`;

    const response = await fetch(url);

    if (!response.ok) {
      throw new Error(`Failed to fetch packages: ${response.statusText}`);
    }

    const data = await response.json();

    // Handle response structure
    let packagesData: Package[];

    if (Array.isArray(data)) {
      packagesData = data;
    } else if (data && Array.isArray(data.packages)) {
      packagesData = data.packages;
    } else if (data && Array.isArray(data.data)) {
      packagesData = data.data;
    } else {
      console.warn("Unexpected API response structure:", data);
      packagesData = [];
    }

    // ✅ PUBLIC: Return all packages from public endpoint (they're already filtered)
    return packagesData;
  }
  // ==================== ADMIN METHODS ====================

  // Admin method to get ALL packages including inactive/deleted
  async getAdminPackages(
    includeInactive: boolean = true,
    includeDeleted: boolean = true,
  ): Promise<Package[]> {
    const token = localStorage.getItem("token");
    const url = `${API_BASE}/subscriptions/admin/packages?includeInactive=${includeInactive}&includeDeleted=${includeDeleted}`;

    const response = await fetch(url, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
      credentials: "include",
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch admin packages: ${response.statusText}`);
    }

    const data = await response.json();

    // Handle response structure for admin endpoint
    let packagesData: Package[];

    if (Array.isArray(data)) {
      packagesData = data;
    } else if (data && Array.isArray(data.packages)) {
      packagesData = data.packages;
    } else {
      console.warn("Unexpected admin packages response structure:", data);
      packagesData = [];
    }

    // ✅ ADMIN: Return ALL packages as requested (no filtering)
    return packagesData;
  }

  // Get job seeker packages (for regular users) - PUBLIC
  async getJobSeekerPackages(): Promise<Package[]> {
    return this.getPackages("job_seeker");
  }

  // Get whitelist packages (for users wanting whitelist access) - PUBLIC
  async getWhitelistPackages(): Promise<Package[]> {
    return this.getPackages("whitelist_request");
  }

  // Get all active packages (admin use) - ADMIN VERSION
  async getAllPackages(): Promise<Package[]> {
    return this.getAdminPackages(true, true);
  }

  // ==================== PAYMENT METHODS ====================

  // Create subscription
  async createSubscription(
    packageId: string,
    isAnnual: boolean,
    paymentMethod: PaymentMethod = "mpesa",
  ): Promise<{ success: boolean; data?: any; message?: string }> {
    const response = await fetch(`${API_BASE}/subscriptions`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      credentials: "include",
      body: JSON.stringify({
        packageId,
        isAnnual,
        paymentMethod,
      }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || "Failed to create subscription");
    }

    return response.json();
  }

  // For M-Pesa payments (user subscription) - enhanced with package type
  async initiateMpesaPayment(
    packageId: string,
    amount: number,
    isAnnual: boolean,
    phoneNumber?: string,
    packageType?: "job_seeker" | "whitelist_request",
  ): Promise<PaymentResponse> {
    const payload: {
      packageId: string;
      amount: number;
      isAnnual: boolean;
      phoneNumber?: string;
      packageType?: "job_seeker" | "whitelist_request";
    } = {
      packageId,
      amount,
      isAnnual,
    };

    if (phoneNumber) {
      payload.phoneNumber = phoneNumber;
    }

    if (packageType) {
      payload.packageType = packageType;
    }

    const response = await fetch(`${API_BASE}/subscriptions/payments/mpesa`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      credentials: "include",
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || "Failed to initiate M-Pesa payment");
    }

    return response.json();
  }

  // Enhanced payment initiation with proper typing
  async initiatePayment(
    request: PaymentInitiationRequest,
  ): Promise<PaymentInitiationResponse> {
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(
        `${API_BASE}/subscriptions/payment/initiate`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            ...(token && { Authorization: `Bearer ${token}` }),
          },
          credentials: "include",
          body: JSON.stringify(request),
        },
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || "Payment initiation failed");
      }

      return {
        success: true,
        message: data.message || "Payment initiated successfully",
        data,
      };
    } catch (error) {
      console.error("Payment initiation error:", error);
      return {
        success: false,
        message:
          error instanceof Error ? error.message : "Payment initiation failed",
      };
    }
  }

  // Convenience method for payment initiation with individual parameters
  async initiatePaymentWithParams(
    planId: string,
    billingPeriod: BillingPeriod,
    paymentMethod: PaymentMethod,
    amount: number,
    phoneNumber?: string,
    currency: string = "KES",
  ): Promise<PaymentInitiationResponse> {
    const request: PaymentInitiationRequest = {
      planId,
      billingPeriod,
      paymentMethod,
      amount,
      currency,
      ...(phoneNumber && { phoneNumber }),
    };

    return this.initiatePayment(request);
  }

  async getSubscriptionStatus(): Promise<SubscriptionResponse> {
    const response = await fetch(`${API_BASE}/subscriptions/status`, {
      credentials: "include",
    });

    if (!response.ok) {
      throw new Error("Failed to fetch subscription status");
    }

    return response.json();
  }

  // Check payment status
  async checkPaymentStatus(transactionId: string): Promise<{
    status: string;
    transactionId: string;
    success: boolean;
    message?: string;
  }> {
    const response = await fetch(
      `${API_BASE}/payments/status/${transactionId}`,
      {
        credentials: "include",
      },
    );

    if (!response.ok) {
      throw new Error("Failed to check payment status");
    }

    return response.json();
  }

  // ==================== ADMIN MANAGEMENT METHODS ====================

  // Admin methods for package management
  async createPackage(
    pkgData: Omit<Package, "id" | "createdAt" | "updatedAt">,
  ): Promise<Package> {
    const token = localStorage.getItem("token");
    const response = await fetch(`${API_BASE}/subscriptions/admin/packages`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      credentials: "include",
      body: JSON.stringify(pkgData),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || "Failed to create package");
    }

    return response.json();
  }

  async updatePackage(pkg: Package): Promise<Package> {
    const token = localStorage.getItem("token");
    const response = await fetch(
      `${API_BASE}/subscriptions/admin/packages/${pkg.id}`,
      {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        credentials: "include",
        body: JSON.stringify(pkg),
      },
    );

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || "Failed to update package");
    }

    return response.json();
  }

  async deletePackage(
    packageId: string,
  ): Promise<{ success: boolean; message: string }> {
    const token = localStorage.getItem("token");
    const response = await fetch(
      `${API_BASE}/subscriptions/admin/packages/${packageId}`,
      {
        method: "DELETE",
        credentials: "include",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      },
    );

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || "Failed to delete package");
    }

    return response.json();
  }

  // ==================== UTILITY METHODS ====================

  // Utility method to get package by ID
  async getPackageById(packageId: string): Promise<Package | null> {
    try {
      const packages = await this.getAllPackages();
      return packages.find((pkg) => pkg.id === packageId) || null;
    } catch (error) {
      console.error("Error fetching package by ID:", error);
      return null;
    }
  }

  // Method to calculate savings for annual plans
  calculateAnnualSavings(
    monthlyPrice: number,
    annualPrice: number,
  ): {
    amount: number;
    percentage: number;
  } {
    const monthlyTotal = monthlyPrice * 12;
    const savings = monthlyTotal - annualPrice;
    const percentage = Math.round((savings / monthlyTotal) * 100);

    return {
      amount: savings,
      percentage,
    };
  }

  // ==================== STATIC METHODS ====================

  // Static method for public packages
  static async getPackagesByType(
    type: "job_seeker" | "whitelist_request" | "all" = "all",
  ): Promise<Package[]> {
    const instance = new PackageService();
    return instance.getPackages(type);
  }

  // Static method for admin packages
  static async getAdminPackages(
    includeInactive: boolean = true,
    includeDeleted: boolean = true,
  ): Promise<Package[]> {
    const instance = new PackageService();
    return instance.getAdminPackages(includeInactive, includeDeleted);
  }

  static async getJobSeekerPackages(): Promise<Package[]> {
    return this.getPackagesByType("job_seeker");
  }

  static async getWhitelistPackages(): Promise<Package[]> {
    return this.getPackagesByType("whitelist_request");
  }

  static async getAllPackages(): Promise<Package[]> {
    return this.getPackagesByType("all");
  }
}

// Export a singleton instance
export const packageService = new PackageService();
